import React, { useEffect, useRef, useState } from "react";

/**
 * SmartImage — perceived-performance image with:
 *   • Branded skeleton placeholder (no layout shift, navy-gradient that matches dark UI)
 *   • Smooth 250ms fade-in on load (no abrupt "pop")
 *   • IntersectionObserver early-fetch (rootMargin defaults to 300px so images start
 *     downloading before they enter the viewport — feels instant during scroll)
 *   • `decoding="async"` + optional `fetchpriority` (high for above-the-fold)
 *   • Graceful fallback: if the image errors, the skeleton stays (instead of a
 *     broken-image icon)
 */
export default function SmartImage({
  src,
  alt = "",
  className = "",
  imgClassName = "",
  priority = false,
  rootMargin = "300px",
  onLoad,
}) {
  const wrapRef = useRef(null);
  const [shouldLoad, setShouldLoad] = useState(priority); // priority images load immediately
  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);

  useEffect(() => {
    if (priority || shouldLoad) return;
    if (!wrapRef.current) return;
    if (typeof IntersectionObserver === "undefined") {
      setShouldLoad(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setShouldLoad(true);
            io.disconnect();
            break;
          }
        }
      },
      { rootMargin },
    );
    io.observe(wrapRef.current);
    return () => io.disconnect();
  }, [priority, shouldLoad, rootMargin]);

  const showImg = shouldLoad && src && !errored;

  return (
    <div
      ref={wrapRef}
      className={`relative overflow-hidden ${className}`}
      aria-busy={!loaded}
      data-testid="smart-image"
    >
      {/* Skeleton placeholder — always rendered, fades out when image is in */}
      <div
        aria-hidden="true"
        className={`absolute inset-0 transition-opacity duration-300 ${
          loaded ? "opacity-0" : "opacity-100"
        }`}
        style={{
          background:
            "linear-gradient(135deg, #0F162A 0%, #0A1128 50%, #1A1F3A 100%)",
        }}
      >
        <div className="absolute inset-0 animate-pulse opacity-40"
             style={{
               background:
                 "radial-gradient(circle at 30% 40%, rgba(212,175,55,0.10), transparent 60%)",
             }} />
      </div>

      {showImg && (
        <img
          src={src}
          alt={alt}
          decoding="async"
          loading={priority ? "eager" : "lazy"}
          fetchPriority={priority ? "high" : "auto"}
          onLoad={(e) => {
            setLoaded(true);
            onLoad?.(e);
          }}
          onError={() => setErrored(true)}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${
            loaded ? "opacity-100" : "opacity-0"
          } ${imgClassName}`}
        />
      )}
    </div>
  );
}
